import { Link, useNavigate } from "react-router-dom";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import type {
  Program,
  TrainingDay,
  WorkoutListItem,
} from "../../../shared/src";
import {
  ApiError,
  deleteWorkout,
  getActiveWorkout,
  listPrograms,
  listRecentWorkouts,
  listTrainingDays,
  startWorkout,
} from "../api-client";

const RECENT_LIMIT = 10;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
});
const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});

function formatStartedAt(iso: string): string {
  const date = new Date(iso);
  return `${dateFormatter.format(date)} · ${timeFormatter.format(date)}`;
}

function formatDuration(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return "";
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function Workouts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const activeWorkoutQuery = useQuery({
    queryKey: ["activeWorkout"] as const,
    queryFn: () => getActiveWorkout(),
  });

  const programsQuery = useQuery({
    queryKey: ["programs", { active: true }] as const,
    queryFn: () => listPrograms({ active: true }),
  });

  const recentQuery = useQuery({
    queryKey: ["recentWorkouts", RECENT_LIMIT] as const,
    queryFn: () => listRecentWorkouts(RECENT_LIMIT),
  });

  const programs = programsQuery.data ?? [];

  const dayQueries = useQueries({
    queries: programs.map((p) => ({
      queryKey: ["trainingDays", p.id] as const,
      queryFn: () => listTrainingDays(p.id),
      enabled: programsQuery.isSuccess,
    })),
  });

  const startMutation = useMutation({
    mutationFn: (dayId: number) => startWorkout({ training_day_id: dayId }),
    onSuccess: (workout) => {
      navigate(`/workouts/${workout.id}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (workoutId: number) => deleteWorkout(workoutId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["recentWorkouts"] }),
        queryClient.invalidateQueries({ queryKey: ["periodSummaries"] }),
        queryClient.invalidateQueries({ queryKey: ["volumeByMuscleGroup"] }),
        queryClient.invalidateQueries({ queryKey: ["prsByPeriod"] }),
        queryClient.invalidateQueries({ queryKey: ["progression"] }),
        queryClient.invalidateQueries({ queryKey: ["activeWorkout"] }),
      ]);
    },
  });

  const active = activeWorkoutQuery.data ?? null;
  const conflictError =
    startMutation.error instanceof ApiError &&
    startMutation.error.code === "CONFLICT"
      ? startMutation.error.message
      : null;

  function onDeleteClicked(row: WorkoutListItem): void {
    const noun = row.set_count === 1 ? "set" : "sets";
    if (
      window.confirm(
        `Delete this workout? ${row.set_count} logged ${noun} will be permanently removed.`,
      )
    ) {
      deleteMutation.mutate(row.id);
    }
  }

  return (
    <main className="min-h-dvh p-4 space-y-5 sm:p-6 sm:space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold sm:text-2xl">Workouts</h1>
        <Link
          to="/dashboard"
          className="text-sm text-slate-400 hover:text-slate-200"
        >
          ← Dashboard
        </Link>
      </header>

      {active && (
        <section
          role="region"
          aria-label="active workout"
          className="rounded-2xl border border-orange-600 bg-orange-950/30 p-4 space-y-2"
        >
          <p className="text-sm font-medium text-orange-200">
            Workout in progress
          </p>
          <Link
            to={`/workouts/${active.id}`}
            className="inline-flex rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500"
          >
            Resume workout →
          </Link>
        </section>
      )}

      {conflictError && (
        <p role="alert" className="text-sm text-red-400">
          {conflictError}
        </p>
      )}

      {programsQuery.isLoading && (
        <p className="text-sm text-slate-400">Loading…</p>
      )}

      {programsQuery.isSuccess && programs.length === 0 && (
        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center space-y-3">
          <p className="text-sm text-slate-300">No active programs yet.</p>
          <p className="text-xs text-slate-500">
            Mark a program as active to start workouts from here.
          </p>
          <Link
            to="/programs"
            className="inline-flex rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"
          >
            Programs
          </Link>
        </section>
      )}

      <div className="space-y-4">
        {programs.map((program, idx) => (
          <ProgramCard
            key={program.id}
            program={program}
            days={dayQueries[idx]?.data ?? []}
            loading={dayQueries[idx]?.isLoading ?? false}
            onStart={(dayId) => startMutation.mutate(dayId)}
            disabled={startMutation.isPending || active !== null}
          />
        ))}
      </div>

      <RecentWorkouts
        rows={recentQuery.data ?? []}
        loading={recentQuery.isLoading}
        onDelete={onDeleteClicked}
        deleting={deleteMutation.isPending}
      />
    </main>
  );
}

function ProgramCard({
  program,
  days,
  loading,
  onStart,
  disabled,
}: {
  program: Program;
  days: TrainingDay[];
  loading: boolean;
  onStart: (dayId: number) => void;
  disabled: boolean;
}) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-3">
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-base font-medium text-slate-100">{program.name}</h2>
        <Link
          to={`/programs/${program.id}`}
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          Edit →
        </Link>
      </header>

      {loading && <p className="text-xs text-slate-500">Loading days…</p>}

      {!loading && days.length === 0 && (
        <p className="text-xs text-slate-500">
          No training days in this program yet.
        </p>
      )}

      <ul className="space-y-2">
        {days.map((d) => (
          <li
            key={d.id}
            className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-950 px-3 py-2"
          >
            <span className="text-sm text-slate-200">{d.name}</span>
            <button
              type="button"
              onClick={() => onStart(d.id)}
              disabled={disabled}
              aria-label={`Start workout for ${d.name}`}
              className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-500 disabled:opacity-50"
            >
              Start
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RecentWorkouts({
  rows,
  loading,
  onDelete,
  deleting,
}: {
  rows: WorkoutListItem[];
  loading: boolean;
  onDelete: (row: WorkoutListItem) => void;
  deleting: boolean;
}) {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 space-y-3">
      <header>
        <h2 className="text-base font-medium text-slate-100">
          Recent workouts
        </h2>
      </header>

      {loading && <p className="text-xs text-slate-500">Loading…</p>}

      {!loading && rows.length === 0 && (
        <p className="text-xs text-slate-500">No logged workouts yet.</p>
      )}

      <ul className="space-y-2">
        {rows.map((row) => {
          const duration = formatDuration(row.started_at, row.ended_at);
          const dayLabel = row.training_day_name ?? "Ad-hoc";
          return (
            <li
              key={row.id}
              className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-950 px-3 py-2"
            >
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="text-sm text-slate-200">
                  {formatStartedAt(row.started_at)}
                </p>
                <p className="text-xs text-slate-500">
                  {duration ? `${dayLabel} · ${duration}` : dayLabel}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onDelete(row)}
                disabled={deleting}
                aria-label={`Delete workout ${row.id}`}
                className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-red-600 hover:text-red-300 disabled:opacity-50"
              >
                Delete
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
